export interface GpuContext {
  device: GPUDevice;
  canvas: HTMLCanvasElement;
  context: GPUCanvasContext;
  /** Format the swapchain expects. Post pass writes to this directly. */
  presentationFormat: GPUTextureFormat;
}

export class WebGpuUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebGpuUnavailableError';
  }
}

export async function initGpu(canvas: HTMLCanvasElement): Promise<GpuContext> {
  if (!('gpu' in navigator)) {
    throw new WebGpuUnavailableError('navigator.gpu missing');
  }
  const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  if (!adapter) {
    throw new WebGpuUnavailableError('no GPU adapter');
  }
  const device = await adapter.requestDevice({
    requiredLimits: {
      maxStorageBuffersPerShaderStage: Math.min(adapter.limits.maxStorageBuffersPerShaderStage, 10),
    },
  });

  device.lost.then((info) => {
    // 'destroyed' fires on normal page teardown / tab hide — not an error.
    if (info.reason === 'destroyed') return;
    console.error('[city-gen] GPUDevice lost:', info.message, info.reason);
  });
  device.addEventListener('uncapturederror', (e) => {
    const ev = e as GPUUncapturedErrorEvent;
    console.error('[city-gen] uncaptured GPU error:', ev.error.message);
  });

  const context = canvas.getContext('webgpu');
  if (!context) {
    throw new WebGpuUnavailableError('canvas.getContext("webgpu") returned null');
  }
  const presentationFormat = navigator.gpu.getPreferredCanvasFormat();
  context.configure({
    device,
    format: presentationFormat,
    alphaMode: 'opaque',
  });

  return { device, canvas, context, presentationFormat };
}

export function showFallback(message?: string): void {
  const el = document.getElementById('fallback');
  if (el) {
    el.classList.add('show');
    if (message) {
      const p = el.querySelector('p:last-child');
      if (p) p.textContent = message;
    }
  }
  const app = document.getElementById('app');
  if (app) app.style.display = 'none';
  const loading = document.getElementById('loading');
  if (loading) loading.remove();
}
